import { VA_FORM_IDS } from '@department-of-veterans-affairs/platform-forms/constants';
import {
  getSessionTrackingProperties,
  trackAction,
} from '../../../../shared/utils/tracking/datadogRumTracking';
import {
  getBddShaSubmissionProperties,
  trackBddShaFormSubmitted,
} from '../bddShaTracking';
import { getConditionsTransformProperties } from './conditionsWorkflowMigrationTracking';
import { getSideNavSubmissionProperties } from './sideNavTracking';

// Strategy: keep one submit entry point and compose payload subsets from
// feature-specific modules so submit wiring stays centralized and extensible.

/**
 * Gathers all submission-time properties for the 21-526EZ_claimSubmitted action
 *
 * Properties include:
 * - formId: the submitted form's ID (constant for this form)
 * - isBdd: whether the submitted form is a BDD claim (from BDD/SHA tracking)
 * - sideNavProperties
 * - conditionsTransformApplied: whether the IPF was transformed (from conditions workflow tracking)
 * - conditionsTransformIpfVersion: the IPF version used for this submission ('v1' or 'v2')
 * - conditionsTransformType: the type of transformation applied (flatten or unflatten)
 * - conditionsTransformTimestamp: when the transformation was applied
 * - sessionCount: number of RUM sessions recorded during this form visit (omitted if none recorded)
 *
 * @param {object} [formData] - The submitted form data
 * @returns {object} The properties to include in the 21-526EZ_claimSubmitted action
 */
const getClaimSubmittedProperties = formData => {
  const { isBdd } = getBddShaSubmissionProperties(formData);
  const sideNavProperties = getSideNavSubmissionProperties();
  const conditionsTransformProperties =
    getConditionsTransformProperties(formData);

  return {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    isBdd,
    ...sideNavProperties,
    ...conditionsTransformProperties,
    ...getSessionTrackingProperties(),
  };
};

/**
 * Tracks all submission-time RUM events through one entry point.
 *
 * Event ownership:
 * - This file orchestrates submit-time event firing.
 * - sideNavTracking owns side-nav-derived submit fields.
 * - bddShaTracking owns BDD/SHA-derived submit fields and BDD-only events.
 *
 * @param {object} [formData] - The submitted form data
 */
export const trackFormSubmitted = (formData = {}) => {
  try {
    const claimSubmittedProperties = getClaimSubmittedProperties(formData);
    const { isBdd, sessionCount } = claimSubmittedProperties;

    trackAction('21-526EZ_claimSubmitted', claimSubmittedProperties);

    trackAction('21-526EZ_numberOfSessions', {
      formId: VA_FORM_IDS.FORM_21_526EZ,
      isBdd,
      ...(sessionCount !== undefined && { sessionCount }),
    });

    trackBddShaFormSubmitted(formData);
  } catch (trackingError) {
    // Silent fail - tracking runs inside the submit success handler, so a
    // tracking error must never turn a successful submission into a failure
  }
};

/**
 * Tracks failed submission attempts with the same conditions workflow
 * properties as the success event (conditionsTransformApplied,
 * conditionsTransformIpfVersion, conditionsTransformType,
 * conditionsTransformTimestamp) so failure rates can be compared by IPF
 * version and transform state.
 *
 * @param {object} [formData] - The submitted form data
 * @param {Error} [error] - The error the submission rejected with
 */
export const trackFormSubmissionFailed = (formData = {}, error) => {
  try {
    const { isBdd } = getBddShaSubmissionProperties(formData);
    const conditionsTransformProperties =
      getConditionsTransformProperties(formData);

    trackAction('21-526EZ_claimSubmissionFailed', {
      formId: VA_FORM_IDS.FORM_21_526EZ,
      isBdd,
      errorMessage: String(error?.message ?? '').slice(0, 500),
      ...conditionsTransformProperties,
    });
  } catch (trackingError) {
    // Silent fail - tracking runs inside the submit rejection handler, so a
    // tracking error must never mask the original submission error
  }
};
